<template>
  <div v-if="state !== 'ready'" class="auth-guard-overlay">
    <!-- Loading State -->
    <div v-if="state === 'loading'" class="loading-state">
      <div class="spinner"></div>
      <p>Checking authentication...</p>
    </div>

    <!-- OIDC Settings Screen (not configured) -->
    <OidcSettingsScreen
      v-else-if="state === 'settings'"
      :initial-settings="oidcSettings"
      @save="handleSettingsSave"
    />

    <!-- Login Screen (not authenticated) -->
    <LoginScreen
      v-else-if="state === 'login'"
      :show="true"
      @login-success="handleLoginSuccess"
      @login-failure="handleLoginFailure"
    />

    <!-- Ready (authenticated) -->
    <div v-else-if="state === 'ready'" class="ready-state">
      <div class="spinner"></div>
      <p>Loading application...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import LoginScreen from './LoginScreen.vue'
import OidcSettingsScreen from './OidcSettingsScreen.vue'
import { useLocalAuthStore, type TokenResult } from '@/stores/localAuthStore'
import { useRbacStore } from '@/stores/rbacStore'

interface OidcSettings {
  oidc_server_url: string
  oidc_issuer: string
  oidc_audience: string
  jwks_url?: string
}

type AuthState = 'loading' | 'settings' | 'login' | 'ready'

const emit = defineEmits<{
  ready: []
  loginSuccess: [token: string]
  settingsSaved: [settings: OidcSettings]
}>()

const authStore = useLocalAuthStore()
const rbacStore = useRbacStore()

// State
const state = ref<AuthState>('loading')
const oidcSettings = ref<OidcSettings>({
  oidc_server_url: '',
  oidc_issuer: '',
  oidc_audience: '',
  jwks_url: ''
})

// Getters
const hasAccessToken = computed(() => !!authStore.accessToken)
const hasValidToken = computed(() => authStore.hasValidToken)
const isTokenExpired = computed(() => authStore.isTokenExpired)

onMounted(async () => {
  await initializeAuth()
})

/**
 * Initialize authentication:
 * 1. Check if OIDC is configured
 * 2. If not configured -> show settings screen
 * 3. If configured -> check token
 * 4. If no valid token -> show login screen
 * 5. If valid token -> ready
 */
async function initializeAuth() {
  try {
    // Step 1: Initialize auth store (restores tokenExpiry from localStorage)
    await authStore.initialize()

    // Step 2: Load OIDC settings from backend
    const settings = await loadOidcSettings()
    
    // Step 3: If no OIDC settings, show settings screen first
    if (!settings || !settings.oidc_issuer || !settings.oidc_audience) {
      console.log('[AuthGuard] OIDC not configured - showing settings')
      state.value = 'settings'
      return
    }

    // OIDC settings exist - load them
    oidcSettings.value = settings

    // Step 4: If we have a valid token, show ready
    if (hasValidToken.value) {
      console.log('[AuthGuard] Valid token exists')
      void rbacStore.fetchMyPermissions()
      state.value = 'ready'
      emit('ready')
      return
    }

    // Step 5: Try to refresh expired token
    if (isTokenExpired.value && authStore.refreshToken) {
      console.log('[AuthGuard] Refreshing token...')
      const refreshed = await authStore.refreshTokens()
      if (refreshed) {
        console.log('[AuthGuard] Token refreshed successfully')
        void rbacStore.fetchMyPermissions()
        state.value = 'ready'
        emit('ready')
        return
      }
    }

    // Step 6: No valid token - show login screen
    console.log('[AuthGuard] No valid token - showing login')
    state.value = 'login'
  } catch (error) {
    console.error('[AuthGuard] Initialization error:', error)
    state.value = 'login'
  }
}

/**
 * Load OIDC settings from backend
 */
async function loadOidcSettings(): Promise<OidcSettings | null> {
  try {
    const response = await fetch('/api/userdata/oidc')
    if (!response.ok) {
      return null
    }
    const data = await response.json()
    return {
      oidc_server_url: data.oidc_server_url || '',
      oidc_issuer: data.oidc_issuer || '',
      oidc_audience: data.oidc_audience || '',
      jwks_url: data.jwks_url || ''
    }
  } catch {
    return null
  }
}

/**
 * Handle settings save from OidcSettingsScreen
 */
async function handleSettingsSave(settings: OidcSettings) {
  try {
    // Save settings to backend
    const response = await fetch('/api/userdata/oidc', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings)
    })

    if (!response.ok) {
      throw new Error('Failed to save settings')
    }

    // Settings saved - now show login screen
    oidcSettings.value = settings
    state.value = 'login'
    
    emit('settingsSaved', settings)
  } catch (error) {
    console.error('[AuthGuard] Failed to save settings:', error)
  }
}

/**
 * Handle login success from LoginScreen
 */
async function handleLoginSuccess(result: TokenResult | void) {
  try {
    // Token is already stored in localStorage by the login process
    // Check if we have a valid token now
    if (authStore.hasValidToken) {
      void rbacStore.fetchMyPermissions()
      state.value = 'ready'
      emit('ready')
      emit('loginSuccess', authStore.accessToken!)
    } else {
      // Login appeared successful but token validation failed
      state.value = 'login'
    }
  } catch (error) {
    console.error('[AuthGuard] Login success error:', error)
    state.value = 'login'
  }
}

/**
 * Handle login failure from LoginScreen
 */
function handleLoginFailure(error: string) {
  console.error('[AuthGuard] Login failure:', error)
  // Stay on login screen
}
</script>

<style scoped>
.auth-guard-overlay {
  position: fixed;
  inset: 0;
  z-index: 10002;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.loading-state,
.ready-state {
  text-align: center;
  color: #ffffff;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #e94560;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

p {
  font-size: 1.1rem;
  color: #a0a0b0;
  margin: 0;
}
</style>
